import { useState } from 'react';
import { useI18n } from '../contexts/I18nContext';
import Layout from '../layout/Layout';
import germanyStepsData from '../locales/germanysteps.json';

type Level = 'bachelor' | 'master';

type LocalizedText = {
  ar?: string;
  en?: string;
};

type DocumentItem = {
  name: LocalizedText;
  details: LocalizedText;
};

type StepItem = {
  step: number;
  key: string;
  title: LocalizedText;
  description: LocalizedText;
  documents?: DocumentItem[];
  note?: LocalizedText;
};

const Icons = {
  Check: () => (
    <svg className="h-full w-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  File: () => (
    <svg className="h-full w-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  ),
  Alert: () => (
    <svg className="h-full w-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

export default function GermanySteps() {
  const { language } = useI18n();
  const [selectedLevel, setSelectedLevel] = useState<Level>('bachelor');

  const data = germanyStepsData.study_in_germany;
  const bachelorData = data.levels.bachelor;
  const masterData = data.levels.master;

  const steps: StepItem[] = (bachelorData.steps ?? []) as StepItem[];

  const documentStep = steps.find((step) => step.documents?.length);
  const requiredDocuments = documentStep?.documents ?? [];

  const getText = (value?: LocalizedText) => {
    if (!value) return '';
    return value[language as keyof LocalizedText] ?? value.en ?? value.ar ?? '';
  };

  const directionClass = language === 'ar' ? 'text-right' : 'text-left';

  return (
    <Layout>
      <section className="bg-[#f5f7fb] pb-6 pt-24 sm:pb-10 sm:pt-28 lg:pt-32">
        <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-[#dfe6f2] bg-white p-5 shadow-sm sm:p-7">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#4f6fa9]">
              {language === 'ar' ? 'الدراسة في ألمانيا' : 'Study in Germany'}
            </p>
            <h1 className="mb-3 text-2xl font-bold leading-tight text-[#13223a] sm:text-3xl">
              {getText(data.title)}
            </h1>
            <p className="text-sm leading-7 text-[#4f5f7b] sm:text-base">
              {getText(data.description)}
            </p>

            <div
              className={`mt-4 rounded-xl border border-[#d8e6ff] bg-[#f3f7ff] p-4 sm:mt-5 sm:p-5 ${directionClass}`}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white text-[#24457a] shadow-sm">
                    <Icons.File />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#13223a] sm:text-base">
                      {language === 'ar' ? 'للمترجمين المعتمدين' : 'Certified translators list'}
                    </p>
                    <p className="mt-1 text-xs text-[#4f5f7b] sm:text-sm">
                      {language === 'ar'
                        ? 'القائمة الرسمية من السفارة الألمانية بالقاهرة (PDF).'
                        : 'Official Arabic list from the German Embassy in Cairo (PDF).'}
                    </p>
                  </div>
                </div>
                <a
                  className="inline-flex items-center justify-center rounded-full bg-[#13223a] px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-[#0f1a2b] sm:text-sm"
                  href="https://kairo.diplo.de/resource/blob/1505052/3f4a653bc3bbbd0941807781ce5708f4/uebersetzerliste-ara--data.pdf"
                  target="_blank"
                  rel="noreferrer"
                >
                  {language === 'ar' ? 'فتح القائمة' : 'Open list'}
                </a>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-2 rounded-xl bg-[#eff3fa] p-1.5 sm:mt-6 sm:max-w-md">
              <button
                onClick={() => setSelectedLevel('bachelor')}
                className={`rounded-lg px-4 py-2.5 text-sm font-semibold transition ${
                  selectedLevel === 'bachelor' ? 'bg-white text-[#13223a] shadow-sm' : 'text-[#4f5f7b]'
                }`}
              >
                {getText(bachelorData.title)}
              </button>
              <button
                onClick={() => setSelectedLevel('master')}
                className={`rounded-lg px-4 py-2.5 text-sm font-semibold transition ${
                  selectedLevel === 'master' ? 'bg-white text-[#13223a] shadow-sm' : 'text-[#4f5f7b]'
                }`}
              >
                {getText(masterData.title)}
              </button>
            </div>
          </div>

          <div className="mt-4 rounded-2xl border border-[#d8e2f2] bg-white p-5 shadow-sm sm:mt-6 sm:p-7">
            <div className="mb-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-[#e8f0ff] p-2 text-[#24457a]">
                <Icons.File />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#13223a] sm:text-2xl">
                  {language === 'ar' ? 'الأوراق المطلوبة' : 'Required Documents'}
                </h2>
                <p className="text-sm text-[#5f6f89] sm:text-base">
                  {language === 'ar'
                    ? `عدد المستندات: ${requiredDocuments.length}`
                    : `Total documents: ${requiredDocuments.length}`}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {requiredDocuments.map((doc, index) => (
                <article
                  key={`${getText(doc.name)}-${index}`}
                  className={`rounded-xl border border-[#e1e8f4] bg-[#fafcff] p-4 sm:p-5 ${directionClass}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 h-6 w-6 flex-shrink-0 rounded-full bg-[#d6ebff] p-1.5 text-[#24457a]">
                      <Icons.Check />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-[#13223a] sm:text-lg">{getText(doc.name)}</h3>
                      <p className="mt-1 text-sm leading-6 text-[#4f5f7b] sm:text-base">{getText(doc.details)}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {documentStep?.note && (
              <div
                className={`mt-4 rounded-xl border border-[#ffd2b8] bg-[#fff6f1] p-4 sm:mt-5 ${directionClass}`}
                role="note"
              >
                <div className="mb-2 flex items-center gap-2 text-[#9c4b17]">
                  <div className="h-5 w-5">
                    <Icons.Alert />
                  </div>
                  <span className="text-sm font-semibold">{language === 'ar' ? 'ملاحظة مهمة' : 'Important Note'}</span>
                </div>
                <p className="text-sm leading-6 text-[#6f3b17] sm:text-base">{getText(documentStep.note)}</p>
              </div>
            )}

            {selectedLevel === 'master' && masterData.extra_requirement && (
              <div
                className={`mt-3 rounded-xl border border-[#cfe4d6] bg-[#f2fbf4] p-4 sm:mt-4 ${directionClass}`}
                role="status"
              >
                <p className="text-sm font-medium text-[#24523b] sm:text-base">{getText(masterData.extra_requirement)}</p>
              </div>
            )}
          </div>

          <div className="mt-4 rounded-2xl border border-[#dfe6f2] bg-white p-5 shadow-sm sm:mt-6 sm:p-7">
            <h2 className="mb-4 text-xl font-bold text-[#13223a] sm:text-2xl">
              {language === 'ar' ? 'خطوات التقديم' : 'Application Steps'}
            </h2>
            <div className="space-y-3 sm:space-y-4">
              {steps.map((step) => (
                <article key={step.step} className={`rounded-xl border border-[#e1e8f4] bg-[#fcfdff] p-4 sm:p-5 ${directionClass}`}>
                  <div className="mb-2 inline-flex items-center rounded-full bg-[#e8f0ff] px-3 py-1 text-xs font-semibold text-[#274777] sm:text-sm">
                    {language === 'ar' ? `الخطوة ${step.step}` : `Step ${step.step}`}
                  </div>
                  <h3 className="text-base font-semibold text-[#13223a] sm:text-lg">{getText(step.title)}</h3>
                  <p className="mt-1 text-sm leading-6 text-[#4f5f7b] sm:text-base">{getText(step.description)}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
